import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Info, RefreshCw } from 'lucide-react';

import { api, ErroApi } from '@/lib/api';
import { formatarData } from '@/lib/utils';
import { mascararCns, mascararCpf } from '@/lib/mascaras';
import {
  ROTULOS_SISTEMA,
  type ChaveHistorico,
  type HistoricoSistema,
  type ItemHistorico,
  type SistemaHistorico,
} from '@/features/historico-paciente/tipos';
import { rotuloDaSituacao, tomDaSituacao } from '@/features/historico-paciente/situacao';
import { Botao } from '@/components/ui/button';
import { Etiqueta } from '@/components/ui/badge';
import { Esqueleto } from '@/components/ui/skeleton';
import {
  Tabela,
  TabelaCabecalho,
  TabelaCelula,
  TabelaCorpo,
  TabelaLinha,
  TabelaTitulo,
  TabelaVazia,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

const SISTEMAS: SistemaHistorico[] = ['esus', 'sisreg'];

/**
 * Consulta de um sistema.
 *
 * Sem refetch automático: cada consulta é auditada, e o histórico não muda
 * enquanto o operador olha a tela. Para atualizar, há o botão.
 */
function useHistorico(sistema: SistemaHistorico, chave: ChaveHistorico) {
  return useQuery({
    queryKey: ['historico-paciente', sistema, chave],
    queryFn: () => api.post<HistoricoSistema>(`/historico-paciente/${sistema}`, chave),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
}

/**
 * Histórico do paciente no eSUS Regulação e no SISREG 3, em duas abas.
 *
 * Os dois sistemas são consultados em paralelo e de forma independente: se um
 * estiver fora do ar, a aba do outro continua mostrando o que tem.
 */
export function HistoricoPaciente({ chave }: { chave: ChaveHistorico }) {
  const [aba, setAba] = React.useState<SistemaHistorico>('esus');
  const idBase = React.useId();

  const consultas = {
    esus: useHistorico('esus', chave),
    sisreg: useHistorico('sisreg', chave),
  };

  return (
    <div className="flex min-w-0 flex-col">
      <div
        className="border-borda mb-4 flex gap-1 overflow-x-auto border-b"
        role="tablist"
        aria-label="Sistema consultado"
      >
        {SISTEMAS.map((sistema) => {
          const { data } = consultas[sistema];
          return (
            <button
              key={sistema}
              id={`${idBase}-aba-${sistema}`}
              type="button"
              role="tab"
              aria-selected={sistema === aba}
              aria-controls={`${idBase}-painel-${sistema}`}
              onClick={() => setAba(sistema)}
              className={cn(
                '-mb-px flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors',
                'focus-visible:outline-foco focus-visible:outline-2 focus-visible:outline-offset-2',
                sistema === aba
                  ? 'border-marca text-marca'
                  : 'text-silenciado-texto hover:text-fundo-texto border-transparent',
              )}
            >
              {ROTULOS_SISTEMA[sistema]}
              {data?.configurado && (
                <Etiqueta tom={sistema === aba ? 'marca' : 'neutro'}>{data.itens.length}</Etiqueta>
              )}
            </button>
          );
        })}
      </div>

      {SISTEMAS.map((sistema) => (
        <div
          key={sistema}
          id={`${idBase}-painel-${sistema}`}
          role="tabpanel"
          aria-labelledby={`${idBase}-aba-${sistema}`}
          hidden={sistema !== aba}
        >
          <PainelSistema sistema={sistema} consulta={consultas[sistema]} />
        </div>
      ))}
    </div>
  );
}

function PainelSistema({
  sistema,
  consulta,
}: {
  sistema: SistemaHistorico;
  consulta: ReturnType<typeof useHistorico>;
}) {
  const { data, error, isPending, isFetching, refetch } = consulta;
  const nome = ROTULOS_SISTEMA[sistema];

  if (isPending) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true" aria-label={`Consultando o ${nome}`}>
        <Esqueleto className="h-16 w-full" />
        <Esqueleto className="h-48 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Aviso tom="erro">
        <p>
          {error instanceof ErroApi
            ? error.mensagemUsuario
            : `Não foi possível consultar o ${nome}.`}
        </p>
        <Botao
          variante="contorno"
          tamanho="sm"
          className="mt-2"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={cn(isFetching && 'animate-spin')} aria-hidden="true" />
          Tentar de novo
        </Botao>
      </Aviso>
    );
  }

  if (!data.configurado) {
    return (
      <Aviso tom="info">
        A integração com o {nome} não está ativa. Peça à administração para configurá-la em{' '}
        {sistema === 'esus' ? 'Integração eSUS' : 'Integração SISREG'}.
      </Aviso>
    );
  }

  if (data.itens.length === 0) {
    return <Aviso tom="info">Nenhum registro deste paciente no {nome}.</Aviso>;
  }

  const solicitacoes = data.itens.filter((item) => item.tipo === 'solicitacao');
  const marcacoes = data.itens.filter((item) => item.tipo === 'marcacao');

  return (
    <div className="flex flex-col gap-4">
      {data.paciente && <ResumoPaciente paciente={data.paciente} />}

      {data.limiteAtingido && (
        <Aviso tom="info">
          O paciente tem mais registros do que a consulta traz de uma vez. Aparecem os mais
          recentes.
        </Aviso>
      )}

      {sistema === 'esus' ? (
        <TabelaHistorico itens={data.itens} />
      ) : (
        <>
          <Secao titulo="Solicitações" quantidade={solicitacoes.length}>
            <TabelaHistorico itens={solicitacoes} vazio="Nenhuma solicitação no SISREG." />
          </Secao>
          <Secao titulo="Marcações" quantidade={marcacoes.length}>
            <TabelaHistorico itens={marcacoes} vazio="Nenhuma marcação no SISREG." />
          </Secao>
        </>
      )}

      <div className="flex justify-end">
        <Botao
          variante="fantasma"
          tamanho="sm"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={cn(isFetching && 'animate-spin')} aria-hidden="true" />
          Atualizar
        </Botao>
      </div>
    </div>
  );
}

function ResumoPaciente({ paciente }: { paciente: NonNullable<HistoricoSistema['paciente']> }) {
  const dados = [
    ['CPF', paciente.cpf ? mascararCpf(paciente.cpf) : null],
    ['CNS', paciente.cns ? mascararCns(paciente.cns) : null],
    ['Nascimento', paciente.dataNascimento ? formatarData(paciente.dataNascimento) : null],
    ['Mãe', paciente.nomeMae],
    ['Município', paciente.municipioResidencia],
  ].filter((par): par is [string, string] => Boolean(par[1]));

  return (
    <div className="bg-silenciado/60 border-borda rounded-lg border p-3">
      <p className="font-medium">{paciente.nome ?? 'Paciente sem nome no cadastro'}</p>
      <dl className="text-silenciado-texto mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {dados.map(([rotulo, valor]) => (
          <div key={rotulo} className="flex gap-1">
            <dt className="font-semibold">{rotulo}:</dt>
            <dd>{valor}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Secao({
  titulo,
  quantidade,
  children,
}: {
  titulo: string;
  quantidade: number;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="flex items-center gap-2 text-sm font-semibold">
        {titulo}
        <Etiqueta>{quantidade}</Etiqueta>
      </h3>
      {children}
    </section>
  );
}

function TabelaHistorico({
  itens,
  vazio = 'Nenhum registro.',
}: {
  itens: ItemHistorico[];
  vazio?: string;
}) {
  return (
    <Tabela>
      <TabelaCabecalho>
        <TabelaLinha>
          <TabelaTitulo>Solicitação</TabelaTitulo>
          <TabelaTitulo>Procedimento</TabelaTitulo>
          <TabelaTitulo>Situação</TabelaTitulo>
          <TabelaTitulo>Atendimento</TabelaTitulo>
        </TabelaLinha>
      </TabelaCabecalho>
      <TabelaCorpo>
        {itens.length === 0 ? (
          <TabelaVazia colunas={4}>{vazio}</TabelaVazia>
        ) : (
          itens.map((item, indice) => (
            <TabelaLinha key={`${item.codigo ?? 'sem-codigo'}-${indice}`}>
              <TabelaCelula rotulo="Solicitação" className="align-top whitespace-nowrap">
                <span className="block">{formatarData(item.dataSolicitacao)}</span>
                {item.codigo && (
                  <span className="text-silenciado-texto block font-mono text-xs">
                    {item.codigo}
                  </span>
                )}
              </TabelaCelula>
              <TabelaCelula rotulo="Procedimento" className="align-top">
                <span className="block">{item.procedimento ?? '—'}</span>
                <Detalhe>{item.cid && `CID ${item.cid}`}</Detalhe>
                <Detalhe>
                  {item.unidadeSolicitante && `Solicitante: ${item.unidadeSolicitante}`}
                </Detalhe>
                <Detalhe>{item.classificacaoRisco && `Risco: ${item.classificacaoRisco}`}</Detalhe>
              </TabelaCelula>
              <TabelaCelula rotulo="Situação" className="align-top">
                <Etiqueta
                  tom={tomDaSituacao(item.situacao)}
                  title={item.situacao ?? undefined}
                  className="whitespace-normal"
                >
                  {rotuloDaSituacao(item.situacao)}
                </Etiqueta>
              </TabelaCelula>
              <TabelaCelula rotulo="Atendimento" className="align-top">
                {item.dataMarcacao && (
                  <span className="block whitespace-nowrap">
                    {formatarData(item.dataMarcacao)}
                    {item.horaMarcacao && ` às ${item.horaMarcacao}`}
                  </span>
                )}
                <span className="block">
                  {[item.unidadeExecutante, item.municipioExecutante].filter(Boolean).join(' — ') ||
                    (item.dataMarcacao ? null : '—')}
                </span>
                <Detalhe>{item.profissionalExecutante}</Detalhe>
              </TabelaCelula>
            </TabelaLinha>
          ))
        )}
      </TabelaCorpo>
    </Tabela>
  );
}

function Detalhe({ children }: { children: React.ReactNode }) {
  if (!children) return null;
  return <span className="text-silenciado-texto block text-xs">{children}</span>;
}

function Aviso({ tom, children }: { tom: 'info' | 'erro'; children: React.ReactNode }) {
  const Icone = tom === 'erro' ? AlertCircle : Info;
  return (
    <div
      role={tom === 'erro' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-2 rounded-md border p-3 text-sm',
        tom === 'erro'
          ? 'border-status-devolvido-borda bg-status-devolvido-fundo text-status-devolvido-texto'
          : 'border-status-enviado-borda bg-status-enviado-fundo text-status-enviado-texto',
      )}
    >
      <Icone className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0 break-words">{children}</div>
    </div>
  );
}
